package er.prateek.crossly.ui.components

import androidx.compose.foundation.Canvas
import androidx.compose.foundation.layout.size
import androidx.compose.runtime.Composable
import androidx.compose.ui.Modifier
import androidx.compose.ui.geometry.CornerRadius
import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.geometry.Size
import androidx.compose.ui.graphics.Brush
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.Path
import androidx.compose.ui.graphics.StrokeCap
import androidx.compose.ui.graphics.StrokeJoin
import androidx.compose.ui.graphics.drawscope.DrawScope
import androidx.compose.ui.graphics.drawscope.Stroke
import androidx.compose.ui.unit.Dp
import androidx.compose.ui.unit.dp
import kotlin.math.PI
import kotlin.math.cos
import kotlin.math.sin

/**
 * The app's icon set, drawn directly on [Canvas].
 *
 * Compose Multiplatform's material-icons artifact isn't a dependency here, and
 * shipping icon fonts/SVGs per target would mean six copies of every asset.
 * Every glyph below is authored on a 24x24 grid and scaled to whatever size the
 * call site asks for, so icons stay crisp at 12dp on a chip and 96dp on the
 * results screen, and they tint straight from the theme.
 */

/** Maps the shared 24x24 authoring grid onto the actual canvas. */
private class IconScope(val draw: DrawScope, val unit: Float, val originX: Float, val originY: Float) {
    fun p(x: Float, y: Float) = Offset(originX + x * unit, originY + y * unit)
    fun s(v: Float) = v * unit
}

private inline fun DrawScope.icon(block: IconScope.() -> Unit) {
    val unit = minOf(size.width, size.height) / 24f
    val originX = (size.width - 24f * unit) / 2f
    val originY = (size.height - 24f * unit) / 2f
    IconScope(this, unit, originX, originY).block()
}

private fun IconScope.stroke(width: Float = 2f) = Stroke(
    width = s(width),
    cap = StrokeCap.Round,
    join = StrokeJoin.Round,
)

/** `p(...)` inside the lambda resolves against the enclosing [IconScope] receiver. */
private fun IconScope.path(build: Path.() -> Unit): Path = Path().apply(build)

@Composable
private fun IconCanvas(size: Dp, modifier: Modifier, draw: DrawScope.() -> Unit) {
    Canvas(modifier = modifier.size(size), onDraw = draw)
}

// ---------------------------------------------------------------- star

/** Star geometry, shared by [IconStar] and the animated rating row. */
fun starPath(center: Offset, outerRadius: Float, innerRadius: Float, points: Int = 5): Path {
    val path = Path()
    val step = PI / points
    // -90deg start puts a point at the top, the orientation players expect.
    var angle = -PI / 2
    repeat(points * 2) { index ->
        val radius = if (index % 2 == 0) outerRadius else innerRadius
        val x = center.x + (radius * cos(angle)).toFloat()
        val y = center.y + (radius * sin(angle)).toFloat()
        if (index == 0) path.moveTo(x, y) else path.lineTo(x, y)
        angle += step
    }
    path.close()
    return path
}

fun DrawScope.drawStar(
    center: Offset,
    radius: Float,
    color: Color,
    brush: Brush? = null,
    innerRatio: Float = 0.44f,
) {
    val path = starPath(center, radius, radius * innerRatio)
    if (brush != null) drawPath(path, brush) else drawPath(path, color)
}

@Composable
fun IconStar(
    size: Dp = 20.dp,
    tint: Color = Color.White,
    modifier: Modifier = Modifier,
    filled: Boolean = true,
) {
    IconCanvas(size, modifier) {
        icon {
            val path = starPath(p(12f, 12.5f), s(10f), s(4.4f))
            if (filled) draw.drawPath(path, tint) else draw.drawPath(path, tint, style = stroke(2f))
        }
    }
}

// ---------------------------------------------------------------- lock

@Composable
fun IconLock(size: Dp = 20.dp, tint: Color = Color.White, modifier: Modifier = Modifier) {
    IconCanvas(size, modifier) {
        icon {
            // Shackle: a half-circle bridging the top corners of the body.
            draw.drawArc(
                color = tint,
                startAngle = 180f,
                sweepAngle = 180f,
                useCenter = false,
                topLeft = p(7f, 3.5f),
                size = Size(s(10f), s(10f)),
                style = stroke(2.2f),
            )
            draw.drawRoundRect(
                color = tint,
                topLeft = p(4.5f, 10f),
                size = Size(s(15f), s(11f)),
                cornerRadius = CornerRadius(s(3f), s(3f)),
            )
        }
    }
}

// ---------------------------------------------------------------- flame

@Composable
fun IconFlame(
    size: Dp = 20.dp,
    tint: Color = Color.White,
    modifier: Modifier = Modifier,
    coreTint: Color? = null,
) {
    IconCanvas(size, modifier) {
        icon {
            // The kink on the left flank (the 7.6,8 -> 10.2,10.4 segment) is what
            // separates a flame from a teardrop — without it this reads as a
            // water drop at small sizes.
            val outer = path {
                moveTo(p(12.5f, 1.8f).x, p(12.5f, 1.8f).y)
                cubicTo(
                    p(16.5f, 6.5f).x, p(16.5f, 6.5f).y,
                    p(19.5f, 9.5f).x, p(19.5f, 9.5f).y,
                    p(19.5f, 14.2f).x, p(19.5f, 14.2f).y,
                )
                cubicTo(
                    p(19.5f, 18.6f).x, p(19.5f, 18.6f).y,
                    p(16.2f, 22.2f).x, p(16.2f, 22.2f).y,
                    p(12f, 22.2f).x, p(12f, 22.2f).y,
                )
                cubicTo(
                    p(7.8f, 22.2f).x, p(7.8f, 22.2f).y,
                    p(4.5f, 18.6f).x, p(4.5f, 18.6f).y,
                    p(4.5f, 14.2f).x, p(4.5f, 14.2f).y,
                )
                cubicTo(
                    p(4.5f, 11.5f).x, p(4.5f, 11.5f).y,
                    p(6f, 9.6f).x, p(6f, 9.6f).y,
                    p(7.6f, 8f).x, p(7.6f, 8f).y,
                )
                cubicTo(
                    p(8.2f, 9.4f).x, p(8.2f, 9.4f).y,
                    p(9.2f, 10.2f).x, p(9.2f, 10.2f).y,
                    p(10.2f, 10.4f).x, p(10.2f, 10.4f).y,
                )
                cubicTo(
                    p(10.9f, 7.2f).x, p(10.9f, 7.2f).y,
                    p(11.4f, 4.4f).x, p(11.4f, 4.4f).y,
                    p(12.5f, 1.8f).x, p(12.5f, 1.8f).y,
                )
                close()
            }
            draw.drawPath(outer, tint)
            coreTint?.let { core ->
                val inner = path {
                    moveTo(p(12f, 11.5f).x, p(12f, 11.5f).y)
                    cubicTo(
                        p(14.8f, 14.2f).x, p(14.8f, 14.2f).y,
                        p(15.6f, 16.2f).x, p(15.6f, 16.2f).y,
                        p(14.8f, 18.2f).x, p(14.8f, 18.2f).y,
                    )
                    cubicTo(
                        p(14f, 20.2f).x, p(14f, 20.2f).y,
                        p(10.6f, 20.6f).x, p(10.6f, 20.6f).y,
                        p(9.5f, 18.6f).x, p(9.5f, 18.6f).y,
                    )
                    cubicTo(
                        p(8.6f, 16.9f).x, p(8.6f, 16.9f).y,
                        p(9.6f, 14.2f).x, p(9.6f, 14.2f).y,
                        p(12f, 11.5f).x, p(12f, 11.5f).y,
                    )
                    close()
                }
                draw.drawPath(inner, core)
            }
        }
    }
}

// ---------------------------------------------------------------- bell

@Composable
fun IconBell(size: Dp = 20.dp, tint: Color = Color.White, modifier: Modifier = Modifier) {
    IconCanvas(size, modifier) {
        icon {
            // The dome: an arc bridging up to the hanger, mirrored back down to the flared base.
            val body = path {
                moveTo(p(6f, 15f).x, p(6f, 15f).y)
                lineTo(p(6f, 10.5f).x, p(6f, 10.5f).y)
                cubicTo(
                    p(6f, 6.4f).x, p(6f, 6.4f).y,
                    p(8.7f, 3.5f).x, p(8.7f, 3.5f).y,
                    p(12f, 3.5f).x, p(12f, 3.5f).y,
                )
                cubicTo(
                    p(15.3f, 3.5f).x, p(15.3f, 3.5f).y,
                    p(18f, 6.4f).x, p(18f, 6.4f).y,
                    p(18f, 10.5f).x, p(18f, 10.5f).y,
                )
                lineTo(p(18f, 15f).x, p(18f, 15f).y)
                cubicTo(
                    p(18f, 16.4f).x, p(18f, 16.4f).y,
                    p(19.2f, 17.3f).x, p(19.2f, 17.3f).y,
                    p(20.2f, 18f).x, p(20.2f, 18f).y,
                )
                lineTo(p(3.8f, 18f).x, p(3.8f, 18f).y)
                cubicTo(
                    p(4.8f, 17.3f).x, p(4.8f, 17.3f).y,
                    p(6f, 16.4f).x, p(6f, 16.4f).y,
                    p(6f, 15f).x, p(6f, 15f).y,
                )
                close()
            }
            draw.drawPath(body, tint)
            // Clapper, hinted at the base rather than fully drawn -- keeps the glyph readable small.
            draw.drawCircle(color = tint, radius = s(1.6f), center = p(12f, 20.6f))
        }
    }
}

// ---------------------------------------------------------------- chevrons & arrows

@Composable
fun IconChevron(
    size: Dp = 20.dp,
    tint: Color = Color.White,
    modifier: Modifier = Modifier,
    pointsLeft: Boolean = true,
) {
    IconCanvas(size, modifier) {
        icon {
            val near = if (pointsLeft) 9f else 15f
            val far = if (pointsLeft) 16f else 8f
            draw.drawPath(
                path {
                    moveTo(p(far, 4f).x, p(far, 4f).y)
                    lineTo(p(near, 12f).x, p(near, 12f).y)
                    lineTo(p(far, 20f).x, p(far, 20f).y)
                },
                tint,
                style = stroke(2.6f),
            )
        }
    }
}

@Composable
fun IconPlay(size: Dp = 20.dp, tint: Color = Color.White, modifier: Modifier = Modifier) {
    IconCanvas(size, modifier) {
        icon {
            draw.drawPath(
                path {
                    moveTo(p(7f, 4.5f).x, p(7f, 4.5f).y)
                    lineTo(p(19.5f, 12f).x, p(19.5f, 12f).y)
                    lineTo(p(7f, 19.5f).x, p(7f, 19.5f).y)
                    close()
                },
                tint,
            )
        }
    }
}

/** Circular arrow — "play again". */
@Composable
fun IconReplay(size: Dp = 20.dp, tint: Color = Color.White, modifier: Modifier = Modifier) {
    IconCanvas(size, modifier) {
        icon {
            draw.drawArc(
                color = tint,
                startAngle = -50f,
                sweepAngle = 290f,
                useCenter = false,
                topLeft = p(3.5f, 3.5f),
                size = Size(s(17f), s(17f)),
                style = stroke(2.3f),
            )
            draw.drawPath(
                path {
                    moveTo(p(17f, 2.5f).x, p(17f, 2.5f).y)
                    lineTo(p(19.5f, 8.5f).x, p(19.5f, 8.5f).y)
                    lineTo(p(13.2f, 7.6f).x, p(13.2f, 7.6f).y)
                    close()
                },
                tint,
            )
        }
    }
}

// ---------------------------------------------------------------- controls

@Composable
fun IconCheck(size: Dp = 20.dp, tint: Color = Color.White, modifier: Modifier = Modifier) {
    IconCanvas(size, modifier) {
        icon {
            draw.drawPath(
                path {
                    moveTo(p(4.5f, 12.5f).x, p(4.5f, 12.5f).y)
                    lineTo(p(10f, 18f).x, p(10f, 18f).y)
                    lineTo(p(19.5f, 6.5f).x, p(19.5f, 6.5f).y)
                },
                tint,
                style = stroke(2.8f),
            )
        }
    }
}

/** Lightbulb — "reveal one letter". */
@Composable
fun IconBulb(size: Dp = 20.dp, tint: Color = Color.White, modifier: Modifier = Modifier) {
    IconCanvas(size, modifier) {
        icon {
            draw.drawCircle(tint, radius = s(6.2f), center = p(12f, 9.5f))
            draw.drawRoundRect(
                color = tint,
                topLeft = p(9f, 15.5f),
                size = Size(s(6f), s(3f)),
                cornerRadius = CornerRadius(s(1.2f), s(1.2f)),
            )
            draw.drawRoundRect(
                color = tint,
                topLeft = p(9.8f, 19f),
                size = Size(s(4.4f), s(2.4f)),
                cornerRadius = CornerRadius(s(1.2f), s(1.2f)),
            )
        }
    }
}

/** Eye — "reveal the whole grid". */
@Composable
fun IconEye(size: Dp = 20.dp, tint: Color = Color.White, modifier: Modifier = Modifier) {
    IconCanvas(size, modifier) {
        icon {
            draw.drawPath(
                path {
                    moveTo(p(2f, 12f).x, p(2f, 12f).y)
                    quadraticTo(p(12f, 3.5f).x, p(12f, 3.5f).y, p(22f, 12f).x, p(22f, 12f).y)
                    quadraticTo(p(12f, 20.5f).x, p(12f, 20.5f).y, p(2f, 12f).x, p(2f, 12f).y)
                    close()
                },
                tint,
                style = stroke(2.2f),
            )
            draw.drawCircle(tint, radius = s(3.1f), center = p(12f, 12f))
        }
    }
}

/** Pointing hand used by the play-screen coach to mime a drag. */
@Composable
fun IconHand(size: Dp = 20.dp, tint: Color = Color.White, modifier: Modifier = Modifier) {
    IconCanvas(size, modifier) {
        icon {
            val finger = path {
                moveTo(p(10f, 3.5f).x, p(10f, 3.5f).y)
                lineTo(p(10f, 13f).x, p(10f, 13f).y)
            }
            draw.drawPath(finger, tint, style = stroke(2.2f))
            val palm = path {
                moveTo(p(10f, 11.5f).x, p(10f, 11.5f).y)
                lineTo(p(7.2f, 13.2f).x, p(7.2f, 13.2f).y)
                cubicTo(
                    p(5.2f, 14.6f).x, p(5.2f, 14.6f).y,
                    p(5.4f, 17.8f).x, p(5.4f, 17.8f).y,
                    p(8.2f, 19.5f).x, p(8.2f, 19.5f).y,
                )
                lineTo(p(16.5f, 19.5f).x, p(16.5f, 19.5f).y)
                cubicTo(
                    p(19.2f, 19.5f).x, p(19.2f, 19.5f).y,
                    p(20.5f, 17.6f).x, p(20.5f, 17.6f).y,
                    p(20.2f, 15.4f).x, p(20.5f, 15.4f).y,
                )
                lineTo(p(18.8f, 11.2f).x, p(18.8f, 11.2f).y)
                cubicTo(
                    p(18.4f, 10f).x, p(18.4f, 10f).y,
                    p(17.2f, 9.4f).x, p(17.2f, 9.4f).y,
                    p(16.2f, 10f).x, p(16.2f, 10f).y,
                )
                lineTo(p(14.2f, 11.5f).x, p(14.2f, 11.5f).y)
                lineTo(p(13.6f, 6.2f).x, p(13.6f, 6.2f).y)
                cubicTo(
                    p(13.4f, 4.8f).x, p(13.4f, 4.8f).y,
                    p(12.2f, 3.8f).x, p(12.2f, 3.8f).y,
                    p(10.8f, 3.8f).x, p(10.8f, 3.8f).y,
                )
                cubicTo(
                    p(9.4f, 3.8f).x, p(9.4f, 3.8f).y,
                    p(8.5f, 5f).x, p(8.5f, 5f).y,
                    p(8.6f, 6.4f).x, p(8.6f, 6.4f).y,
                )
                lineTo(p(10f, 13f).x, p(10f, 13f).y)
            }
            draw.drawPath(palm, tint, style = stroke(2f))
            draw.drawCircle(tint, radius = s(1.15f), center = p(10f, 3.2f))
        }
    }
}

@Composable
fun IconBackspace(size: Dp = 20.dp, tint: Color = Color.White, modifier: Modifier = Modifier) {
    IconCanvas(size, modifier) {
        icon {
            draw.drawPath(
                path {
                    moveTo(p(8.5f, 4.5f).x, p(8.5f, 4.5f).y)
                    lineTo(p(21.5f, 4.5f).x, p(21.5f, 4.5f).y)
                    lineTo(p(21.5f, 19.5f).x, p(21.5f, 19.5f).y)
                    lineTo(p(8.5f, 19.5f).x, p(8.5f, 19.5f).y)
                    lineTo(p(1.5f, 12f).x, p(1.5f, 12f).y)
                    close()
                },
                tint,
                style = stroke(2f),
            )
            draw.drawLine(tint, p(11.5f, 9f), p(18f, 15f), s(2f), StrokeCap.Round)
            draw.drawLine(tint, p(18f, 9f), p(11.5f, 15f), s(2f), StrokeCap.Round)
        }
    }
}

/** Sliders — the Settings entry point. */
@Composable
fun IconSliders(size: Dp = 20.dp, tint: Color = Color.White, modifier: Modifier = Modifier) {
    IconCanvas(size, modifier) {
        icon {
            val rows = listOf(6f to 15f, 12f to 9f, 18f to 16.5f)
            rows.forEach { (y, knobX) ->
                draw.drawLine(tint, p(3f, y), p(21f, y), s(2f), StrokeCap.Round)
                draw.drawCircle(tint, radius = s(3.1f), center = p(knobX, y))
            }
        }
    }
}

@Composable
fun IconTrophy(size: Dp = 20.dp, tint: Color = Color.White, modifier: Modifier = Modifier) {
    IconCanvas(size, modifier) {
        icon {
            // Handles first so the cup body overlaps them cleanly.
            draw.drawArc(
                color = tint,
                startAngle = 90f,
                sweepAngle = 180f,
                useCenter = false,
                topLeft = p(1.5f, 3.5f),
                size = Size(s(6f), s(7f)),
                style = stroke(2f),
            )
            draw.drawArc(
                color = tint,
                startAngle = -90f,
                sweepAngle = 180f,
                useCenter = false,
                topLeft = p(16.5f, 3.5f),
                size = Size(s(6f), s(7f)),
                style = stroke(2f),
            )
            draw.drawPath(
                path {
                    moveTo(p(5.5f, 2.5f).x, p(5.5f, 2.5f).y)
                    lineTo(p(18.5f, 2.5f).x, p(18.5f, 2.5f).y)
                    lineTo(p(18.5f, 9f).x, p(18.5f, 9f).y)
                    cubicTo(
                        p(18.5f, 13.5f).x, p(18.5f, 13.5f).y,
                        p(15.5f, 15.5f).x, p(15.5f, 15.5f).y,
                        p(12f, 15.5f).x, p(12f, 15.5f).y,
                    )
                    cubicTo(
                        p(8.5f, 15.5f).x, p(8.5f, 15.5f).y,
                        p(5.5f, 13.5f).x, p(5.5f, 13.5f).y,
                        p(5.5f, 9f).x, p(5.5f, 9f).y,
                    )
                    close()
                },
                tint,
            )
            draw.drawRoundRect(
                color = tint,
                topLeft = p(10.5f, 15f),
                size = Size(s(3f), s(4f)),
                cornerRadius = CornerRadius(s(1f), s(1f)),
            )
            draw.drawRoundRect(
                color = tint,
                topLeft = p(6.5f, 19f),
                size = Size(s(11f), s(2.8f)),
                cornerRadius = CornerRadius(s(1.4f), s(1.4f)),
            )
        }
    }
}

// ---------------------------------------------------------------- shapes (riddle algebra)

/** Filled rounded square — the "□" variable in shape-algebra riddles. */
@Composable
fun IconSquare(size: Dp = 20.dp, tint: Color = Color.White, modifier: Modifier = Modifier) {
    IconCanvas(size, modifier) {
        icon {
            draw.drawRoundRect(
                color = tint,
                topLeft = p(4f, 4f),
                size = Size(s(16f), s(16f)),
                cornerRadius = CornerRadius(s(3.5f), s(3.5f)),
            )
        }
    }
}

/** Filled circle — the "○" variable. */
@Composable
fun IconCircle(size: Dp = 20.dp, tint: Color = Color.White, modifier: Modifier = Modifier) {
    IconCanvas(size, modifier) {
        icon { draw.drawCircle(color = tint, radius = s(8.5f), center = p(12f, 12f)) }
    }
}

/** Filled triangle — the "△" variable. */
@Composable
fun IconTriangle(size: Dp = 20.dp, tint: Color = Color.White, modifier: Modifier = Modifier) {
    IconCanvas(size, modifier) {
        icon {
            draw.drawPath(
                path {
                    moveTo(p(12f, 3.5f).x, p(12f, 3.5f).y)
                    lineTo(p(20.5f, 20f).x, p(20.5f, 20f).y)
                    lineTo(p(3.5f, 20f).x, p(3.5f, 20f).y)
                    close()
                },
                tint,
            )
        }
    }
}

/** Filled diamond — the mystery operator in "crack the rule" riddles. */
@Composable
fun IconDiamond(size: Dp = 20.dp, tint: Color = Color.White, modifier: Modifier = Modifier) {
    IconCanvas(size, modifier) {
        icon {
            draw.drawPath(
                path {
                    moveTo(p(12f, 3f).x, p(12f, 3f).y)
                    lineTo(p(20f, 12f).x, p(20f, 12f).y)
                    lineTo(p(12f, 21f).x, p(12f, 21f).y)
                    lineTo(p(4f, 12f).x, p(4f, 12f).y)
                    close()
                },
                tint,
            )
        }
    }
}

/** Filled heart — one of the "fruit" variables in symbol-algebra riddles. */
@Composable
fun IconHeart(size: Dp = 20.dp, tint: Color = Color.White, modifier: Modifier = Modifier) {
    IconCanvas(size, modifier) {
        icon {
            draw.drawPath(
                path {
                    moveTo(p(12f, 20.5f).x, p(12f, 20.5f).y)
                    cubicTo(p(5f, 15.5f).x, p(5f, 15.5f).y, p(2.5f, 12f).x, p(2.5f, 12f).y, p(2.5f, 8.5f).x, p(2.5f, 8.5f).y)
                    cubicTo(p(2.5f, 5.5f).x, p(2.5f, 5.5f).y, p(4.8f, 3.5f).x, p(4.8f, 3.5f).y, p(7.5f, 3.5f).x, p(7.5f, 3.5f).y)
                    cubicTo(p(9.4f, 3.5f).x, p(9.4f, 3.5f).y, p(11f, 4.6f).x, p(11f, 4.6f).y, p(12f, 6.2f).x, p(12f, 6.2f).y)
                    cubicTo(p(13f, 4.6f).x, p(13f, 4.6f).y, p(14.6f, 3.5f).x, p(14.6f, 3.5f).y, p(16.5f, 3.5f).x, p(16.5f, 3.5f).y)
                    cubicTo(p(19.2f, 3.5f).x, p(19.2f, 3.5f).y, p(21.5f, 5.5f).x, p(21.5f, 5.5f).y, p(21.5f, 8.5f).x, p(21.5f, 8.5f).y)
                    cubicTo(p(21.5f, 12f).x, p(21.5f, 12f).y, p(19f, 15.5f).x, p(19f, 15.5f).y, p(12f, 20.5f).x, p(12f, 20.5f).y)
                    close()
                },
                tint,
            )
        }
    }
}

/**
 * The Crossly mark: a 3x3 crossword fragment where the filled squares spell out
 * a crossing — the app's identity in one glyph, tinted from the live palette.
 */
@Composable
fun IconLogoMark(
    size: Dp = 48.dp,
    tint: Color,
    accentTint: Color,
    blockTint: Color,
    modifier: Modifier = Modifier,
) {
    IconCanvas(size, modifier) {
        icon {
            val gap = 1.1f
            val cell = (24f - gap * 2f) / 3f
            // true = accent (the crossing), false = plain tile, null = blocked square.
            val layout = listOf(
                listOf(false, true, null),
                listOf(true, true, true),
                listOf(null, true, false),
            )
            layout.forEachIndexed { row, cells ->
                cells.forEachIndexed { col, kind ->
                    val x = col * (cell + gap)
                    val y = row * (cell + gap)
                    draw.drawRoundRect(
                        color = when (kind) {
                            true -> accentTint
                            false -> tint
                            null -> blockTint
                        },
                        topLeft = p(x, y),
                        size = Size(s(cell), s(cell)),
                        cornerRadius = CornerRadius(s(1.6f), s(1.6f)),
                    )
                }
            }
        }
    }
}
